import { useCallback, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";

import { projectsSortStart } from "Store/Actions";
import { hasNoElements, removeElementAt, hasNoElement } from "Utils/Array";

import { IOverviewColumn } from "Model/Configuration/IOverviewColumns";
import { InternalFieldNames } from "Components/Overview/Grid/InternalFieldNames";
import { TranslationContext } from "Components/Generic/Translator";
import { ConfigurationContext } from "Components/Generic/Configuration";
import { Translator } from "Utils/Translator";
import { Configuration } from "Utils/Configuration";
import { getTranslatedShortFieldLabel } from "Utils/Field";
import { EntityType } from "Model/EntityType";
import { selectSortedColumns } from "Store/Selectors/Projects";
import { isTrue } from "Utils/Boolean";
import { onRenderHeader } from "../Header";
import { onRender } from "../Renderer";
import { IExtendedColumn } from "../IExtendedColumn";
import { IUseSortedColumnsProps } from "./IUseSortedColumsProps";

const REQUIRED_COLUMNS: IOverviewColumn[] = [
  {
    fieldName: InternalFieldNames.DataSource,
    maxWidth: 80,
    minWidth: 80
  },
  {
    fieldName: InternalFieldNames.Name,
    maxWidth: 500,
    minWidth: 250
  },
  {
    fieldName: InternalFieldNames.AddressValidation,
    maxWidth: 32,
    minWidth: 32
  },
  {
    fieldName: InternalFieldNames.DuplicateCheck,
    maxWidth: 32,
    minWidth: 32
  },
  {
    fieldName: InternalFieldNames.Exported,
    maxWidth: 32,
    minWidth: 32
  }
];
export function useSortedColumns(): IUseSortedColumnsProps {
  const dispatch = useDispatch();
  const translator = useContext(TranslationContext);
  const config = useContext(ConfigurationContext);
  const [columnList, setColumnList] = useState(generateColumnList(config, translator));
  const [sortColumnList, setSortColumnList] = useState<[string, boolean][]>(useSelector(selectSortedColumns));

  const onSort = useCallback(
    (event?: React.MouseEvent<HTMLElement>, column?: IExtendedColumn) => {
      const isShiftKey = event?.shiftKey ?? false;

      let updatedSortColumnList = [...sortColumnList];
      const updatedColumnList = columnList.map((col: IExtendedColumn) => {
        const sortedColumn = { ...col };
        const isColumn = sortedColumn.key === column.key;
        const wasPreviouslySorted = sortedColumn.key !== column.key && col.isSorted;

        if (isColumn) {
          sortedColumn.isSorted = true;
          sortedColumn.isSortedDescending = !(sortedColumn.isSortedDescending ?? true);
          updatedSortColumnList = addSortColumn(updatedSortColumnList, [
            sortedColumn.key,
            sortedColumn.isSortedDescending
          ]);
        } else if (wasPreviouslySorted && isShiftKey) {
          // ignore
        } else {
          sortedColumn.isSorted = false;
          sortedColumn.isSortedDescending = undefined;
          updatedSortColumnList = removeSortColumn(updatedSortColumnList, sortedColumn.key);
        }

        return sortedColumn;
      });

      setColumnList(updatedColumnList);
      setSortColumnList(updatedSortColumnList);
      dispatch(projectsSortStart(updatedSortColumnList));
    },
    [dispatch, sortColumnList, columnList]
  );

  return {
    onSort,
    columnList
  };
}

function generateColumnList(config: Configuration, translator: Translator): IExtendedColumn[] {
  const columnConfigList = addRequiredColumns(config.getOverviewColumns());
  return columnConfigList.map((columnConfig) => generateColumn(columnConfig, translator));
}

function addRequiredColumns(columnConfigList: IOverviewColumn[]): IOverviewColumn[] {
  for (const column of REQUIRED_COLUMNS) {
    if (hasNoElement(columnConfigList, (c) => c.fieldName === column.fieldName)) {
      columnConfigList.push(column);
    }
  }

  return columnConfigList;
}

function generateColumn(columnConfig: IOverviewColumn, translator: Translator): IExtendedColumn {
  const { fieldName } = columnConfig;
  return {
    ...columnConfig,
    isResizable: isTrue(columnConfig.isResizable, true),
    key: fieldName,
    name: getTranslatedShortFieldLabel(fieldName, EntityType.Project, translator),
    onRender,
    onRenderHeader
  };
}

function addSortColumn(sortColumnList: [string, boolean][], sortColumn: [string, boolean]): [string, boolean][] {
  let isUpdate = false;
  const [fieldName, isSortedDescending] = sortColumn;
  const updatedSortColumnList = sortColumnList.map<[string, boolean]>(([fN, desc]) => {
    if (fN === fieldName) {
      isUpdate = true;
      return [fN, isSortedDescending];
    }

    return [fN, desc];
  });

  if (isUpdate) {
    return updatedSortColumnList;
  }

  updatedSortColumnList.push(sortColumn);
  return updatedSortColumnList;
}

function removeSortColumn(sortColumnList: [string, boolean][], fieldName: string): [string, boolean][] {
  if (hasNoElements(sortColumnList)) {
    return [];
  }

  const index = sortColumnList.findIndex(([fN]) => fN === fieldName);
  if (index < 0) {
    return sortColumnList;
  }

  return removeElementAt(sortColumnList, index);
}
